// src/pages/LoginScreen.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Divider,
  Link,
  Container,
} from "@mui/material";
import {
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  Login,
  Shield,
  CheckCircle,
  TrendingUp,
  LocationOn,
  Phone,
} from "@mui/icons-material";
import { useAuth } from "../../context/AuthContext";

function LoginScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, loading: authLoading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || "/";
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    
    if (!email.trim() || !password.trim()) {
      setError("Please enter both email and password");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await login(email, password);
      
      if (!result.success) {
        // Handle API error message
        const errorMsg = result.error || "Login failed. Please try again.";
        setError(errorMsg);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || 
                      err.response?.data?.detail ||
                      err.message || 
                      "Login failed. Please try again.";
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          bgcolor: "#F8FAFC",
        }}
      >
        <CircularProgress sx={{ color: "#2563EB" }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#F1F5F9",
        py: 3,
        px: 2,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: -200,
          right: -200,
          width: 500,
          height: 500,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(37, 99, 235, 0.06) 0%, transparent 70%)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -200,
          left: -200,
          width: 400,
          height: 400,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(37, 99, 235, 0.04) 0%, transparent 70%)",
        }}
      />

      <Container maxWidth="lg">
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: "0 20px 60px rgba(0,0,0,0.06)",
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            bgcolor: "#FFFFFF",
            position: "relative",
            maxWidth: "95%",
            mx: "auto",
          }}
        >
          {/* Left Panel */}
          <Box
            sx={{
              display: { xs: "none", lg: "flex" },
              flexDirection: "column",
              justifyContent: "space-between",
              width: { lg: "45%" },
              background: "linear-gradient(145deg, #0B1120 0%, #1A2332 50%, #0F172A 100%)",
              p: 5,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: 0.03,
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              }}
            />

            <Box
              sx={{
                position: "absolute",
                top: "10%",
                right: "-10%",
                width: 300,
                height: 300,
                borderRadius: "50%",
                background: "radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)",
                animation: "float 12s ease-in-out infinite",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: "10%",
                left: "-10%",
                width: 250,
                height: 250,
                borderRadius: "50%",
                background: "radial-gradient(circle, rgba(59, 130, 246, 0.08) 0%, transparent 70%)",
                animation: "float 15s ease-in-out infinite reverse",
              }}
            />

            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Box 
                sx={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: 1.5, 
                  mb: 4,
                  bgcolor: "rgba(255,255,255,0.05)",
                  borderRadius: 2,
                  p: 1.5,
                  width: "fit-content",
                }}
              >
                <Box
                  component="img"
                  src="/images/allforcare-logo.png"
                  alt="Allforcare"
                  sx={{
                    height: 72,
                    width: "auto",
                    objectFit: "contain",
                    display: "block",
                  }}
                />
              </Box>

              <Typography
                variant="h3"
                sx={{
                  color: "#FFFFFF",
                  fontWeight: 800,
                  mb: 1.5,
                  fontSize: "2.2rem",
                  lineHeight: 1.2,
                  letterSpacing: "-0.02em",
                }}
              >
                Welcome Back
                <Box
                  component="span"
                  sx={{
                    display: "block",
                    background: "linear-gradient(135deg, #93C5FD, #3B82F6)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  to Allforcare
                </Box>
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: "rgba(255,255,255,0.55)",
                  lineHeight: 1.8,
                  fontSize: "0.9rem",
                  maxWidth: "90%",
                  fontWeight: 300,
                }}
              >
                Streamline your care management with real-time insights and efficient scheduling.
              </Typography>

              <Box sx={{ mt: 4, display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      bgcolor: "rgba(59, 130, 246, 0.15)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CheckCircle sx={{ color: "#60A5FA", fontSize: 14 }} />
                  </Box>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>
                    Real-time visit tracking
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      bgcolor: "rgba(59, 130, 246, 0.15)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <TrendingUp sx={{ color: "#60A5FA", fontSize: 14 }} />
                  </Box>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>
                    Comprehensive care management
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      bgcolor: "rgba(59, 130, 246, 0.15)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Shield sx={{ color: "#60A5FA", fontSize: 14 }} />
                  </Box>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>
                    Secure & fully compliant
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                position: "relative",
                zIndex: 1,
                borderTop: "1px solid rgba(255,255,255,0.05)",
                pt: 2.5,
                mt: 3,
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <LocationOn sx={{ color: "rgba(255,255,255,0.25)", fontSize: 14 }} />
                  <Typography
                    variant="caption"
                    sx={{
                      color: "rgba(255,255,255,0.3)",
                      fontSize: "0.7rem",
                    }}
                  >
                    15 Maswell Park Road, Hounslow, TW3 2DL
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Phone sx={{ color: "rgba(255,255,255,0.25)", fontSize: 14 }} />
                  <Typography
                    variant="caption"
                    sx={{
                      color: "rgba(255,255,255,0.3)",
                      fontSize: "0.7rem",
                    }}
                  >
                    +44 (0) 20 1234 5678
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Right Panel */}
          <Box
            sx={{
              flex: 1,
              p: { xs: 3, sm: 4, lg: 5 },
              bgcolor: "#FFFFFF",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                display: { xs: "flex", lg: "none" },
                justifyContent: "center",
                mb: 3,
              }}
            >
              <Box
                component="img"
                src="/images/allforcare-logo.png"
                alt="Allforcare"
                sx={{
                  height: 56,
                  width: "auto",
                  objectFit: "contain",
                }}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "#0F172A",
                  mb: 0.5,
                  letterSpacing: "-0.02em",
                  fontSize: "1.5rem",
                }}
              >
                Sign In
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#64748B", fontWeight: 400, fontSize: "0.85rem" }}
              >
                Enter your credentials to access your account
              </Typography>
            </Box>

            {error && (
              <Alert
                severity="error"
                sx={{ mb: 2.5, borderRadius: 2, fontSize: "0.85rem" }}
                onClose={() => setError(null)}
              >
                {error}
              </Alert>
            )}

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={32} sx={{ color: "#2563EB" }} />
              </Box>
            ) : (
              <form onSubmit={submitHandler}>
                <TextField
                  fullWidth
                  label="Email or Username"
                  variant="outlined"
                  type="text"
                  placeholder="Enter your email or username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  size="small"
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      bgcolor: "#F8FAFC",
                      fontSize: "0.9rem",
                      "&:hover": {
                        bgcolor: "#F1F5F9",
                      },
                      "&.Mui-focused": {
                        bgcolor: "#FFFFFF",
                        boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.06)",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#64748B",
                      fontSize: "0.85rem",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: "#94A3B8", fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Password"
                  variant="outlined"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  size="small"
                  sx={{
                    mb: 1.5,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      bgcolor: "#F8FAFC",
                      fontSize: "0.9rem",
                      "&:hover": {
                        bgcolor: "#F1F5F9",
                      },
                      "&.Mui-focused": {
                        bgcolor: "#FFFFFF",
                        boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.06)",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#64748B",
                      fontSize: "0.85rem",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: "#94A3B8", fontSize: 18 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleClickShowPassword}
                          edge="end"
                          sx={{ color: "#94A3B8", padding: 0.5 }}
                        >
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    mb: 2.5,
                  }}
                >
                  <Link
                    href="#"
                    variant="body2"
                    sx={{
                      color: "#2563EB",
                      textDecoration: "none",
                      fontWeight: 500,
                      fontSize: "0.8rem",
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Forgot Password?
                  </Link>
                </Box>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  endIcon={<Login fontSize="small" />}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    bgcolor: "#2563EB",
                    transition: "all 0.3s",
                    "&:hover": {
                      bgcolor: "#1D4ED8",
                      boxShadow: "0 4px 16px rgba(37, 99, 235, 0.3)",
                    },
                    "&:disabled": {
                      bgcolor: "#93C5FD",
                    },
                  }}
                >
                  Sign In
                </Button>
              </form>
            )}

            <Divider sx={{ my: 3 }}>
              <Typography
                variant="caption"
                sx={{ color: "#94A3B8", px: 2, fontWeight: 500, fontSize: "0.7rem" }}
              >
                Secure Login
              </Typography>
            </Divider>

            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="caption"
                sx={{ color: "#94A3B8", fontSize: "0.7rem" }}
              >
                By signing in, you agree to our Terms of Service and Privacy Policy
              </Typography>
            </Box>

            <Box
              sx={{
                display: { xs: "block", lg: "none" },
                mt: 2.5,
                pt: 2.5,
                borderTop: "1px solid #E2E8F0",
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignItems: "center" }}>
                <Typography
                  variant="caption"
                  sx={{ color: "#94A3B8", fontSize: "0.65rem" }}
                >
                  15 Maswell Park Road, Hounslow, TW3 2DL
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "#94A3B8", fontSize: "0.6rem" }}
                >
                  © 2024 Allforcare. All rights reserved.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Container>

      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translate(0, 0) scale(1); }
            33% { transform: translate(15px, -15px) scale(1.05); }
            66% { transform: translate(-10px, 10px) scale(0.95); }
          }
        `}
      </style>
    </Box>
  );
}

export default LoginScreen;